import { Button } from 'components/atoms';
import { Form } from 'components/organisms';
import {
  saleChannelInitialValue,
  SaleChannelTabs,
} from 'components/organisms/forms/sales-channel/SalesChannelForm';
import { FormikHelpers } from 'formik';
import { useMutateOptions } from 'hooks';
import { useParamId } from 'hooks/useParamId';
import middleware from 'middleware';
import { useTranslation } from 'react-i18next';
import { PiFloppyDisk, PiX } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetSalesChannel, usePutSaleChannel } from 'services/salesChannel';
import { usePostSalesChannelImage } from 'services/salesChannel/image';
import { ISaleChannelForm } from 'services/salesChannel/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { getLastPathname } from 'utils/navbar/getParentPathname';
import { onErrorShowToast } from 'utils/service/errorHandler';

const EditSaleChannel = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const salesChannelId = useParamId('settings/sales-channel');
  const { pathname } = useLocation();

  const updateSalesChannel = usePutSaleChannel();

  const { data: saleChannel, isLoading } = useGetSalesChannel(salesChannelId);
  const mutateOptions = useMutateOptions<ISaleChannelForm>();

  const addSaleChannelImage = usePostSalesChannelImage();

  const onGoToDetail = () =>
    navigate(`/settings/sales-channel/${salesChannelId}`);

  const onCreateSaleChannelImage = async (
    file: File,
    isPredefined: boolean
  ) => {
    await addSaleChannelImage.mutateAsync(
      { file, id: salesChannelId },
      {
        onSuccess: () => isPredefined && onGoToDetail(),
        onError: error => onErrorShowToast(error, 'toast-message.add-file'),
      }
    );
  };

  const submitForm = async (
    values: ISaleChannelForm,
    helpers: FormikHelpers<ISaleChannelForm>
  ) => {
    if (values.file) {
      onCreateSaleChannelImage(values.file, values.isPredefined);
    }

    await updateSalesChannel.mutateAsync(
      {
        id: salesChannelId,
        ...middleware.saleChannel.formToQuery(values),
      },
      {
        onSuccess: onGoToDetail,
        ...mutateOptions(helpers),
      }
    );
  };

  return (
    <Form.SaleChannel
      i18nextPrefix='label'
      initialValues={
        saleChannel
          ? middleware.saleChannel.queryToForm(saleChannel)
          : saleChannelInitialValue
      }
      onSubmit={submitForm}
      disableTabs={[SaleChannelTabs.providers]}
      customBackItem={
        <Button
          onlyIcon
          onClick={() => {
            navigate(
              `/settings/sales-channel/${getLastPathname(pathname)}/providers`
            );
          }}>
          <Button.Description description={t('button.cancel')} />

          <Button.Icon icon={<PiX />} />
        </Button>
      }
      topbarContent={
        <Button type='submit' variant={BUTTON_VARIANT.submit}>
          <Button.Text text={t('button.save')} />
          <Button.Icon icon={<PiFloppyDisk />} />
        </Button>
      }
      tittle={saleChannel?.name || '-'}
      backPath={pathname}
      status={{ isLoading }}
    />
  );
};

export default EditSaleChannel;
